/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { Tooltip } from 'antd'
import Text from 'src/components/ts-components/Text'
import MapMarkerIcon from '../../../../icons/MapMarkerIcon'
import { getGameTypeBadge } from '@sportninja/common/features/game/gameTypeColors'
import { centerInfoStyles, gameTypeTagStyles, venueRowStyles } from '../styles'
import { Game, GameState, GameType } from '../types'

interface CenterInfoProps {
  game: Game
  fullGameType: GameType | undefined
  gameState: GameState
  currentPeriod: any
  timezoneData: ReturnType<
    typeof import('@sportninja/common/hooks/useTimezoneManager').useTimezoneManager
  >
  isMySchedule: boolean
  statusText: string
}

const CenterInfo = ({
  game,
  fullGameType,
  gameState,
  currentPeriod,
  timezoneData,
  isMySchedule,
  statusText,
}: CenterInfoProps) => {
  const {
    userDate,
    locationDate,
    checkLocationDateAndUserDateAreSame,
    getDateString,
  } = timezoneData

  const scheduleName = isMySchedule
    ? game.rootSchedule?.name
    : game.schedule?.name

  // Null when the game has no type. An unrecognised one still resolves, with
  // the neutral fallback fill — same contract as the event category badge.
  const gameTypeBadge = getGameTypeBadge(fullGameType?.name)

  const renderInProgressTime = () => {
    if (!gameState.gameStarted || gameState.gameOver) {
      return null
    }

    if (gameState.isGenericSport) {
      return (
        <Text
          variant='paragraphSmall'
          weight='medium'
          color={colors.NEUTRAL_0}
          style='text-transform: uppercase;'
        >
          In Progress
        </Text>
      )
    }

    if (gameState.hasShootout) {
      return (
        <Text
          variant='paragraphSmall'
          weight='medium'
          color={colors.NEUTRAL_0}
          style='text-transform: uppercase;'
        >
          Shootout
        </Text>
      )
    }

    const periodDisplay = currentPeriod.clockTime
      ? currentPeriod.fullName
      : currentPeriod.name

    const timeDisplay = currentPeriod.clockTime
      ? ` - ${currentPeriod.clockTime}`
      : ` ${t('util:capitalize', { text: t('GameScreen:intermission', {}) })}`

    return (
      <Text
        variant='paragraphSmall'
        weight='medium'
        color={colors.NEUTRAL_0}
        style='text-transform: uppercase;'
      >
        {periodDisplay}
        {timeDisplay}
      </Text>
    )
  }

  return (
    <div css={centerInfoStyles}>
      {scheduleName && (
        <Text
          variant='captionXSmall'
          weight='semiBold'
          color={colors.NEUTRAL_50}
          style='text-transform: uppercase;'
        >
          {scheduleName}
        </Text>
      )}

      {/* The game number and the "(if necessary)" qualifier stay as a caption
          line — they modify the game, not its kind. The TYPE itself moved into
          a badge below, matching the event row's category badge: both answer
          "what kind of thing is this?" and sit in the same slot, so they read
          as one system. */}
      {(game?.league_game_number || game.if_necessary_status) && (
        <Text
          variant='captionXSmall'
          weight='semiBold'
          color={colors.NEUTRAL_50}
          style='text-transform: uppercase;'
        >
          {game?.league_game_number ? `Game #${game.league_game_number}` : ''}
          {game.if_necessary_status && (
            <span
              css={css`
                text-transform: lowercase;
              `}
            >
              {game?.league_game_number ? ' ' : ''}(if Necessary)
            </span>
          )}
        </Text>
      )}

      {gameTypeBadge && (
        <span
          css={gameTypeTagStyles}
          style={{
            backgroundColor: gameTypeBadge.color,
            color: gameTypeBadge.textColor,
          }}
        >
          {gameTypeBadge.label}
        </span>
      )}

      <div>
        {gameState.isGameTimeToBeDefined ? (
          <Tooltip title='Game Time to be Defined'>
            <Text
              variant='paragraphSmall'
              weight='medium'
              color={colors.NEUTRAL_0}
            >
              TBD
            </Text>
          </Tooltip>
        ) : (
          <>
            <Text
              variant='paragraphSmall'
              weight='medium'
              color={colors.NEUTRAL_0}
            >
              {userDate &&
                getDateString(userDate, 'MMMM dd, y - h:mm a (ZZZZ)')}
            </Text>
            {locationDate && !checkLocationDateAndUserDateAreSame() && (
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
                style='text-align: center;'
              >
                {getDateString(
                  locationDate,
                  userDate?.toFormat?.('dd') !== locationDate?.toFormat?.('dd')
                    ? 'MMM d - h:mm a ZZZZ'
                    : 'h:mm a ZZZZ'
                )}
              </Text>
            )}
          </>
        )}
      </div>

      {renderInProgressTime()}

      {gameState.gameOver && statusText && (
        <Text
          variant='paragraphSmall'
          weight='bold'
          color={colors.PRIMARY}
          style='text-transform: uppercase;'
        >
          {statusText}
        </Text>
      )}

      {game?.venue?.name && (
        <div css={venueRowStyles}>
          <MapMarkerIcon size={16} color={colors.NEUTRAL_50} />
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {game.venue.name}
            {game.facility ? ` - ${game.facility.name}` : ''}
          </Text>
        </div>
      )}
    </div>
  )
}

export default CenterInfo
